import { Loading, ExpiresAtCountdown, SuperCheckbox } from "components";
import messages from "i18n/messages";
import first from "lodash/first";
import flowRight from "lodash/flowRight";
import isEmpty from "lodash/isEmpty";
import { makeSelectDateByOrderId } from "logic/dates";
import { makeSelectEventByOrderId } from "logic/events";
import { isValidEmail, makeSelectCustomer } from "logic/customers";
import { makeSelectCurOrg } from "logic/orgs";
import { updateOrderAttr } from "logic/orders";
import { calcTotal, makeSelectLoading } from "logic/payment";
import PropTypes from "prop-types";
import { Component } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Button, FormText, Label } from "reactstrap";
import { bindActionCreators } from "redux";
import { createStructuredSelector } from "reselect";
import update from "immutability-helper";
import { atsxRegex } from "components/OescvContactForm";

const ATSX_REGEX = "Please provide a valid ATSX # like 'AUT1234F'";

const getQueryParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const params = {};
  if (urlParams.get("r") === "1") params.r = true;
  if (urlParams.get("p") === "1") params.p = true;
  return params;
};

const salesEnabled = ({ date }) => {
  const queryParams = getQueryParams();
  if (!(date && date.sale_enabled)) return false;

  return Object.keys(queryParams).length > 0 ? queryParams.p : true;
};

const resEnabled = ({ date }) => {
  const queryParams = getQueryParams();
  if (!(date && date.res_enabled)) return false;

  return Object.keys(queryParams).length > 0 ? queryParams.r : true;
};

const BookButtons = (props) => (
  <div className="book-btns">
    {salesEnabled(props) ? <ContinuePaymentButton {...props} /> : null}
    {resEnabled(props) ? (
      <ReservationNowButton
        {...props}
        handleSubmit={props.handleSubmitReservation}
      />
    ) : null}
  </div>
);

const PayButton = ({
  color,
  outline,
  block,
  canContinue,
  label,
  loading,
  handleSubmit,
}) => (
  <Button
    color={color}
    outline={outline}
    block={block}
    className="tt-primary-bg-color tt-primary-border-color"
    disabled={!canContinue || loading}
    onClick={handleSubmit}
  >
    {loading ? (
      <Loading color={outline ? "primary" : "white"} width="1.5em" />
    ) : (
      <FormattedMessage {...label} />
    )}
  </Button>
);

const ContinuePaymentButton = ({
  status,
  total,
  date,
  handleSubmit,
  canContinue,
  loading,
}) => (
  <>
    {status && status === "missing_data" ? (
      <div className="text-center p-2">
        <FormText color="danger">
          <FormattedMessage {...messages.pleaseAgreeTerms} />
        </FormText>
      </div>
    ) : null}
    {status && status.includes("loading") ? (
      <div className="text-center p-2">
        <Loading color="primary" />
        {status ? (
          <div>
            <Label color="primary">
              <FormattedMessage {...messages[status]} />
            </Label>
          </div>
        ) : null}
      </div>
    ) : (
      <PayButton
        color="success"
        block={date && !date.res_enabled}
        loading={loading}
        label={total ? messages.continueToPayment : messages.buyNow}
        handleSubmit={handleSubmit}
        canContinue={canContinue}
      />
    )}
  </>
);

const ReservationNowButton = ({
  date,
  status,
  handleSubmit,
  canContinue,
  loading,
}) => (
  <>
    {status && status === "missing_data" ? (
      <div className="text-center p-2">
        <FormText color="danger">
          <FormattedMessage {...messages.pleaseAgreeTerms} />
        </FormText>
      </div>
    ) : null}
    {status && status.includes("loading") ? (
      <div className="text-center p-2">
        <Loading color="primary" />
        {status ? (
          <div>
            <Label color="primary">
              <FormattedMessage {...messages[status]} />
            </Label>
          </div>
        ) : null}
      </div>
    ) : (
      <PayButton
        color={date.sale_enabled ? "primary" : "success"}
        block={date && !date.sale_enabled}
        outline={date.sale_enabled ? true : false}
        label={messages.makeReservation}
        loading={loading}
        handleSubmit={handleSubmit}
        canContinue={canContinue}
      />
    )}
  </>
);

class PayBookNowButtons extends Component {
  state = {
    error: null,
  };

  handleToggleRes = () =>
    this.props.actions.updateOrderAttr("res", !this.props.order.res);

  canContinue = () => {
    if (this.props.status === "missing_data") return false;

    const { customer, date } = this.props;

    if (!this.props.agreedTerms) return false;
    if (isEmpty(customer.email)) return false;
    if (date.pp_delivery && !customer.meta.pp_delivery_slot) return false;
    if (date.pp_delivery && !customer.phone) return false;
    if (customer.meta && customer.meta.atsx_num) {
      if (new RegExp(atsxRegex).test(customer && customer.meta.atsx_num))
        return true; // shortcut to exit for oescv
      if (this.state.error !== ATSX_REGEX)
        this.setState(update(this.state, { error: { $set: ATSX_REGEX } }));
    }
    if (isEmpty(customer.firstname)) return false;
    if (isEmpty(customer.lastname)) return false;
    if (!isValidEmail(customer.email)) return false;
    if (this.props.deliveryMethod === "post" && customer.addresses.length > 0) {
      if (isEmpty(customer.addresses[0].street)) return false;
      if (isEmpty(customer.addresses[0].city)) return false;
      if (isEmpty(customer.addresses[0].country_code)) return false;
      if (isEmpty(customer.addresses[0].postcode)) return false;
    }
    return true;
  };

  render() {
    const { date, order, loading, status, values } = this.props;

    return (
      <div className="payment-summary payment-summary-wrap book-btns-wrap">
        {this.state.error ? (
          <div className="alert alert-warning">
            {messages[this.state.error] ? (
              <FormattedMessage {...messages[this.state.error]} />
            ) : (
              this.state.error
            )}
          </div>
        ) : null}
        {status !== "done" && (!order || !order.res) ? (
          <BookButtons
            status={status}
            handleSubmit={this.props.handleSubmit}
            canContinue={this.canContinue()}
            total={calcTotal(values)}
            loading={loading}
            date={date}
            handleSubmitReservation={this.props.handleSubmitReservation}
          />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  org: makeSelectCurOrg(),
  loading: makeSelectLoading(),
  date: makeSelectDateByOrderId((props) =>
    props.order ? first(props.order.event_date_ids) : null
  ),
  event: makeSelectEventByOrderId((props) =>
    props.order ? first(props.order.event_date_ids) : null
  ),
  customer: makeSelectCustomer(),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateOrderAttr,
    },
    dispatch
  ),
});

const enhance = flowRight(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
);

PayBookNowButtons.propTypes = {
  values: PropTypes.array.isRequired,
  status: PropTypes.string,
  deliveryMethod: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  handleSubmitReservation: PropTypes.func,
};

export default enhance(PayBookNowButtons);
